import React, { useMemo } from 'react';
import VisaApplicationFilters from './VisaApplicationFilters';
import {
  filterVisaApplications,
  getApplicantName,
} from '../utils/visaApplicationFilters';
import { formatStatusLabel, getStatusBadgeClass } from '../utils/visaApplicationStatus';

const VisaApplicationsPoolSection = ({
  title,
  subtitle,
  applications,
  filters,
  onFiltersChange,
  onClearFilters,
  showAirlineFilter = false,
  showManagerFilter = false,
  managerOptions = [],
  showAirlineColumn = false,
  onView,
  onViewHistory,
  onUpdateStatus,
  emptyMessage = 'No applications in this pool yet.',
  headerExtra = null,
}) => {
  const filteredApplications = useMemo(
    () => filterVisaApplications(applications, filters),
    [applications, filters]
  );

  const canUpdateStatus = (app) =>
    onUpdateStatus && ['processing', 'error', 'finalize'].includes(app.application_status);

  const colSpan = showAirlineColumn ? 9 : 8;

  return (
    <>
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="primary-title">{title}</h1>
            <p className="text-ink-secondary mt-1">{subtitle}</p>
          </div>
          {headerExtra}
        </div>
      </div>

      <VisaApplicationFilters
        applications={applications}
        filters={filters}
        onChange={onFiltersChange}
        onClear={onClearFilters}
        showAirlineFilter={showAirlineFilter}
        showManagerFilter={showManagerFilter}
        managerOptions={managerOptions}
        hideStatusFilter
        totalCount={applications.length}
        filteredCount={filteredApplications.length}
      />

      <div className="table-shell">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {showAirlineColumn && (
                  <th>Airline</th>
                )}
                <th>Passenger ID</th>
                <th>Applicant</th>
                <th>From</th>
                <th>To</th>
                <th>Visa Type</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="text-center py-8 text-ink-muted">
                    {emptyMessage}
                  </td>
                </tr>
              ) : filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="text-center py-8 text-ink-muted">
                    No applications match your filters.
                  </td>
                </tr>
              ) : (
                filteredApplications.map((app) => (
                  <tr key={app.id}>
                    {showAirlineColumn && (
                      <td className="font-semibold text-ink">{app.airline_name}</td>
                    )}
                    <td className="font-semibold text-ink">{app.passenger_id}</td>
                    <td>
                      {getApplicantName(app.customer_visa_application_data)}
                    </td>
                    <td>
                      {app.from_country_name || app.from_country}
                    </td>
                    <td>
                      {app.to_country_name || app.to_country}
                    </td>
                    <td>{app.visa_type}</td>
                    <td>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}
                      >
                        {formatStatusLabel(app.application_status)}
                      </span>
                    </td>
                    <td>
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => onView(app)}
                          className="action-chip"
                        >
                          View
                        </button>
                        {onViewHistory && (
                          <button
                            type="button"
                            onClick={() => onViewHistory(app)}
                            className="action-chip"
                          >
                            History
                          </button>
                        )}
                        {canUpdateStatus(app) && (
                          <button
                            type="button"
                            onClick={() => onUpdateStatus(app)}
                            className="text-xs px-3 py-1.5 rounded-crm font-medium bg-slate-100 text-ink-table hover:bg-slate-200 transition-colors"
                          >
                            Update Status
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default VisaApplicationsPoolSection;
